import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { keepMapComments } from './index.js';
export const EMPTY_STYLE = { voice: [], avoid: [] };
/** As many rules as a person will actually hold in their head. */
export const MOST_RULES = 15;
/** A rule is a line, not a paragraph. */
export const LONGEST_RULE = 160;
export const STYLE_STARTER = `# How this documentation is written.
#
# Two lists of short rules. They travel with every request for help with the
# words, so a suggestion comes back sounding like the rest of your pages rather
# than like a model — and they are the sentences you already say in review.
#
# Short lines, not paragraphs: a rule a person can hold in their head is a rule
# a model can follow. ${MOST_RULES} of each at most, ${LONGEST_RULE} characters each.
#
# voice:
#   - Write in the present tense.
#   - Address the reader as "you".
#   - One instruction per sentence.
# avoid:
#   - Marketing language.
#   - "simply", "just", "obviously".
`;
const asLines = (value, field) => {
    if (value === undefined || value === null)
        return [];
    if (!Array.isArray(value)) {
        throw new Error(`${field} must be a list of short lines, like \`- Write in the present tense.\``);
    }
    const lines = value.map((entry, index) => {
        if (typeof entry !== 'string' && typeof entry !== 'number') {
            throw new Error(`${field} item ${index + 1} must be a line of text`);
        }
        const line = String(entry).trim();
        if (line === '')
            throw new Error(`${field} item ${index + 1} is empty`);
        if (line.length > LONGEST_RULE) {
            /**
             * Refused rather than cut. Half a rule is a different rule, and the
             * writer who wrote it is the only person who can shorten it correctly.
             */
            throw new Error(`${field} item ${index + 1} is ${line.length} characters — a rule is one line, ` +
                `${LONGEST_RULE} at most`);
        }
        return line;
    });
    if (lines.length > MOST_RULES) {
        throw new Error(`${field} has ${lines.length} rules — ${MOST_RULES} is the most anybody reads`);
    }
    return lines;
};
export function parseStyleGuide(yaml) {
    const raw = yaml.trim() === '' ? {} : parseYaml(yaml);
    if (raw === null || raw === undefined)
        return { voice: [], avoid: [] };
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        throw new Error('style.yaml must be a mapping with two keys: voice and avoid');
    }
    const map = raw;
    for (const key of Object.keys(map)) {
        if (key !== 'voice' && key !== 'avoid') {
            throw new Error(`"${key}" is not a style setting — the keys are voice and avoid`);
        }
    }
    return {
        voice: asLines(map['voice'], 'voice'),
        avoid: asLines(map['avoid'], 'avoid'),
    };
}
/**
 * Back to YAML, keeping whatever somebody wrote beside a rule.
 *
 * The same promise `serializeGlossary` makes and for the same reason: a note
 * explaining WHY a rule exists is the most valuable line in the file, and a
 * save that dropped it would teach people not to write one.
 */
export function serializeStyleGuide(style, previous) {
    const text = stringifyYaml({
        ...(style.voice.length > 0 ? { voice: style.voice } : {}),
        ...(style.avoid.length > 0 ? { avoid: style.avoid } : {}),
    }, { lineWidth: 0 }).trimEnd();
    if (previous === undefined)
        return text === '{}' ? '' : text;
    return keepMapComments(text === '{}' ? '' : text, previous).trimEnd();
}
//# sourceMappingURL=style.js.map